import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  FaPlus,
  FaPalette,
  FaFileAlt,
  FaMousePointer,
  FaChevronDown,
  FaChevronRight,
} from 'react-icons/fa'; // Removed FaThList, FaGlobe as they weren't used for now
import { mockWorkspaces } from '../components/mockAdCreatorData'; // Adjusted import path
import { AdProject, AdPage } from '../components/adCreatorTypes'; // Adjusted import path

const CanvasArea: React.FC<{ ad?: AdProject; selectedPageId?: string }> = ({
  ad,
  selectedPageId,
}) => {
  const page = ad?.pages.find(p => p.id === selectedPageId);
  if (!ad || !page)
    return (
      <div className="flex-grow bg-gray-300 flex items-center justify-center text-gray-500">
        Select an Ad and Page to view
      </div>
    );

  return (
    <div className="flex-grow bg-gray-200 p-4 relative overflow-auto">
      <h3 className="text-center font-semibold mb-2">{page.name} Preview</h3>
      <div
        className="mx-auto bg-white shadow-lg relative"
        style={{
          width: ad.settings?.defaultWidth || 300,
          height: ad.settings?.defaultHeight || 250,
          backgroundColor: page.backgroundColor || '#ffffff',
        }}
      >
        {page.elements.map(el => (
          <div
            key={el.id}
            className="absolute border border-dashed border-gray-400"
            style={{
              left: el.x,
              top: el.y,
              width: el.width,
              height: el.height,
              transform: `rotate(${el.rotation || 0}deg)`,
              opacity: el.opacity,
              zIndex: el.zIndex,
            }}
          >
            {el.type === 'header' && (
              <div
                className="p-1 text-xs truncate"
                style={{ fontSize: el.fontSize, textAlign: el.textAlign, color: el.color }}
              >
                {el.text}
              </div>
            )}
            {el.type === 'image' && (
              <img
                src={el.src}
                alt={el.altText || 'Image'}
                className="w-full h-full"
                style={{ objectFit: el.objectFit }}
                onError={e => (e.currentTarget.style.display = 'none')}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

interface LeftPanelProps {
  ad?: AdProject;
  selectedPageId?: string;
  onSelectPage: (pageId: string) => void;
}

const LeftPanel: React.FC<LeftPanelProps> = ({ ad, selectedPageId, onSelectPage }) => {
  const [activeTab, setActiveTab] = useState<'Pages' | 'Messages' | 'Design'>('Pages');
  const [pagesOpen, setPagesOpen] = useState(true);
  const [addSectionOpen, setAddSectionOpen] = useState(true);

  const languages = ['English', 'Spanish', 'Portuguese'];
  const [selectedLanguage, setSelectedLanguage] = useState(languages[0]);

  return (
    <div className="w-80 bg-white h-full shadow-lg flex flex-col">
      <div className="flex border-b">
        {['Pages', 'Messages', 'Design'].map(tabName => (
          <button
            key={tabName}
            onClick={() => setActiveTab(tabName as any)}
            className={`flex-1 py-3 px-2 text-sm font-medium text-center focus:outline-none 
                        ${activeTab === tabName ? 'border-b-2 border-primary text-primary' : 'text-gray-500 hover:text-gray-700'}`}
          >
            {tabName === 'Pages' && <FaFileAlt className="inline mr-1 mb-px" />}
            {tabName === 'Messages' && <FaMousePointer className="inline mr-1 mb-px" />}
            {tabName === 'Design' && <FaPalette className="inline mr-1 mb-px" />}
            {tabName}
          </button>
        ))}
      </div>

      {activeTab === 'Pages' && (
        <div className="p-4 space-y-4 overflow-y-auto flex-grow">
          <div>
            <label
              htmlFor="language-select"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Language
            </label>
            <select
              id="language-select"
              value={selectedLanguage}
              onChange={e => setSelectedLanguage(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md shadow-sm text-sm focus:ring-primary focus:border-primary"
            >
              {languages.map(lang => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </div>

          <div>
            <button
              onClick={() => setPagesOpen(!pagesOpen)}
              className="w-full flex justify-between items-center py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
            >
              Pages
              {pagesOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
            </button>
            {pagesOpen &&
              ad?.pages.map(page => (
                <div
                  key={page.id}
                  onClick={() => onSelectPage(page.id)}
                  className={`p-2 my-1 text-sm rounded cursor-pointer hover:bg-primary-light ${selectedPageId === page.id ? 'bg-primary-light ring-1 ring-primary text-primary-dark font-semibold' : 'bg-gray-100'}`}
                >
                  {page.name}
                </div>
              ))}
            {pagesOpen && (
              <button className="mt-1 w-full text-left p-2 text-sm text-primary hover:bg-primary-light rounded flex items-center">
                <FaPlus size={12} className="mr-2" /> Add Page
              </button>
            )}
          </div>

          <div>
            <button
              onClick={() => setAddSectionOpen(!addSectionOpen)}
              className="w-full flex justify-between items-center py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded"
            >
              Add Section
              {addSectionOpen ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
            </button>
            {addSectionOpen && (
              <div className="pl-2 mt-1 space-y-1">
                <div className="p-2 text-sm bg-gray-100 rounded hover:bg-gray-200 cursor-pointer">
                  Basic Blocks
                </div>
                <div className="p-2 text-sm bg-gray-100 rounded hover:bg-gray-200 cursor-pointer">
                  Interactive Blocks
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {activeTab === 'Messages' && (
        <div className="p-4 text-gray-500">Messages configuration (Coming Soon)</div>
      )}
      {activeTab === 'Design' && (
        <div className="p-4 text-gray-500">Global design settings (Coming Soon)</div>
      )}
    </div>
  );
};

const AdEditorPage: React.FC = () => {
  const { adId } = useParams<{ adId: string }>();
  const [currentAd, setCurrentAd] = useState<AdProject | undefined>(undefined);
  const [selectedPageId, setSelectedPageId] = useState<string | undefined>(undefined);

  useEffect(() => {
    const adProject = mockWorkspaces.flatMap(ws => ws.adProjects).find(p => p.id === adId);
    setCurrentAd(adProject);
    if (adProject && adProject.pages.length > 0) {
      setSelectedPageId(adProject.pages[0].id);
    }
  }, [adId]);

  if (!currentAd) {
    return (
      <div className="p-4 text-center text-gray-600">
        Loading Ad Project... If this persists, the ad may not exist or data is unavailable.
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <LeftPanel ad={currentAd} selectedPageId={selectedPageId} onSelectPage={setSelectedPageId} />
      <CanvasArea ad={currentAd} selectedPageId={selectedPageId} />
      {/* <div className="w-80 bg-white h-full shadow-lg p-4">Properties Panel (Coming Soon)</div> */}
    </div>
  );
};

export default AdEditorPage;
